import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ClassRoom } from '../classroom.js';
import { Teacher } from '../teacher.js';
import { generateJoinCode, isDuplicateKeyError } from '../utils/join-code.js';

const MAX_CODE_GENERATION_ATTEMPTS = 10;

export const regenerateClassroomCodeMutation = registerMutation({
  operationName: 'regenerateClassroomCodeMutation',
  type: () => ClassRoom,
  params: [{ name: 'classRoomID', type: () => String }],

  resolve: (classRoomID: string) => async (_gqlFields: BaseQLSelectionSet<ClassRoom>) => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    if (!currentUser?._id) {
      throw new Error('Nicht autorisiert: Kein User eingeloggt');
    }

    const teacherCollection = await getCollection(Teacher);
    const teacher = await teacherCollection.findOne({ userID: currentUser._id });
    if (!teacher) {
      throw new Error('Nicht autorisiert: Kein Lehrerprofil gefunden');
    }

    const classroomCollection = await getCollection(ClassRoom);
    const classroom = await classroomCollection.findOne({ _id: classRoomID });
    if (!classroom) {
      throw new Error('Klasse nicht gefunden');
    }
    if (classroom.teacherID !== teacher._id) {
      throw new Error('Nicht autorisiert: Diese Klasse gehört dir nicht');
    }

    // Relies on the unique index on `code` (see ensure-classroom-indexes.ts): update straight away and
    // only retry on an actual collision, instead of checking-then-updating.
    let code: string | undefined;
    for (let attempt = 0; attempt < MAX_CODE_GENERATION_ATTEMPTS; attempt++) {
      const candidate = generateJoinCode();
      try {
        await classroomCollection.updateOne({ _id: classRoomID }, { $set: { code: candidate } });
        code = candidate;
        break;
      } catch (err) {
        if (!isDuplicateKeyError(err)) throw err;
      }
    }
    if (!code) {
      throw new Error('Failed to generate a unique classroom code after 10 attempts');
    }

    return new ClassRoom({ ...classroom, code });
  },
});
